import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Brand } from "../components/Logo";
import { OuiNon } from "../components/FicheParts";
import { Section, Champ, QUESTIONS, TEL } from "./Fiche";

const today = () => new Date().toLocaleDateString("sv-SE");
const VIDE = {
  nom: "", fonction: "", date_naissance: "", telephone: "", email: "", adresse: "",
  sante_probleme: "", sante_details: "", traitement: "", traitement_details: "", allergies: "", allergies_details: "", medical: "",
  medecin_nom: "", medecin_tel: "", urgence_nom: "", urgence_lien: "", urgence_tel: "", taille_maillot: "",
  confirme: false, date_fiche: today(),
};
const input = "w-full min-w-0 rounded-xl border border-line bg-ink px-3 py-2.5 text-base text-chalk outline-none transition-colors focus:border-lime resize-none";
const lbl = "mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-dim";

const valider = (f) => {
  const e = {};
  const req = (k) => { if (!f[k].trim()) e[k] = "Champ obligatoire"; };
  const tel = (k, obligatoire) => { if (f[k].trim() && !TEL.test(f[k].trim())) e[k] = "Numéro invalide"; else if (obligatoire) req(k); };
  tel("telephone", true); tel("medecin_tel", false); tel("urgence_tel", true);
  req("urgence_nom"); req("urgence_lien");
  QUESTIONS.forEach(([k, d]) => { if (f[k] === "oui") req(d); });
  if (!f.confirme) e.confirme = "Merci de cocher cette case";
  if (!f.date_fiche) e.date_fiche = "Champ obligatoire";
  return e;
};

const Merci = () => (
  <div data-testid="fiche-enc-success" className="mt-8 rounded-[24px] border border-lime/60 bg-pitch p-6 text-center">
    <CheckCircle2 className="mx-auto h-12 w-12 text-lime" />
    <h2 className="mt-3 font-display text-3xl uppercase text-chalk">Merci !</h2>
    <p className="mt-2 text-chalk/90">Votre fiche a bien été enregistrée dans l'encadrement du club.</p>
    <Link to="/" data-testid="fiche-enc-home" className="mt-5 inline-block rounded-full bg-lime px-5 py-2.5 text-sm font-semibold text-ink">Retour au site</Link>
  </div>
);

const useFicheEncadrant = (token, setF) => {
  const [etat, setEtat] = useState(token ? "chargement" : "invalide");
  const [msg, setMsg] = useState(token ? "" : "Lien de fiche invalide. Demandez un nouveau lien au staff.");
  useEffect(() => {
    if (!token) return;
    api.get(`/fiches-encadrant/lien/${encodeURIComponent(token)}`).then((r) => {
      const { deja_remplie: deja, ...d } = r.data ?? {};
      setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(d).filter(([k, v]) => k in VIDE && typeof v === "string")) }));
      setEtat(deja ? "modification" : "perso");
    }).catch((x) => { setEtat("invalide"); setMsg(x.response?.data?.detail || "Lien de fiche invalide. Demandez un nouveau lien au staff."); });
  }, [token, setF]);
  return [etat, msg];
};

export default function FicheEncadrant() {
  const [f, setF] = useState(VIDE);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const [token] = useState(() => new URLSearchParams(window.location.search).get("t") ?? "");
  const [etat, lienErr] = useFicheEncadrant(token, setF);
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setErr((e) => ({ ...e, [k]: undefined })); };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = valider(f);
    setErr(e);
    if (Object.keys(e).length) {
      toast.error("Certains champs sont à compléter");
      setTimeout(() => document.querySelector("[aria-invalid=true], [data-error=true]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setBusy(true);
    setServerErr("");
    try {
      const { nom, fonction, ...body } = f;
      await api.post("/fiches-encadrant", { ...body, token });
      setOk(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (x) {
      const d = x.response?.data?.detail;
      const m = Array.isArray(d) ? d[0]?.msg?.replace(/^Value error, /, "") : d || "Envoi impossible, réessayez";
      setServerErr(m);
      toast.error(m);
    } finally { setBusy(false); }
  };

  const p = { f, set, err };
  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-50 border-b border-chalk/5 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-2.5 sm:px-6"><Brand /></div>
      </header>
      <main data-testid="fiche-enc-page" className="mx-auto max-w-3xl px-4 pb-20 pt-8 sm:px-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-lime">Encadrement du club</p>
        <h1 data-testid="fiche-enc-title" className="mt-3 font-display text-4xl uppercase leading-[0.95] text-chalk sm:text-5xl">Fiche de renseignements - Encadrant</h1>
        <p className="mt-3 text-sm text-dim">Les champs marqués <span className="text-signal">*</span> sont obligatoires. Ces informations restent confidentielles et ne sont visibles que par le staff.</p>
        {(etat === "modification" || etat === "perso") && !ok && (
          <p data-testid={`fiche-enc-mode-${etat}`} className="mt-4 rounded-xl border border-lime/60 bg-lime/10 px-4 py-3 text-sm text-chalk">
            Fiche de <strong>{f.nom.toUpperCase()}</strong>{f.fonction && ` (${f.fonction})`} : {etat === "modification" ? "déjà envoyée, corrigez ce qui a changé puis renvoyez-la." : "les informations déjà connues du club sont pré-remplies, complétez le reste."}
          </p>
        )}
        {etat === "invalide" && <p data-testid="fiche-enc-lien-invalide" className="mt-4 rounded-xl border border-signal bg-signal/10 px-4 py-3 text-sm text-chalk">{lienErr}</p>}
        {etat === "chargement" && <p className="mt-6 text-dim">Chargement de la fiche…</p>}
        {etat === "chargement" || etat === "invalide" ? null : ok ? <Merci /> : (
          <form onSubmit={submit} noValidate className="mt-8 space-y-5">
            <Section n={1} title="Vos coordonnées" testid="fiche-enc-section-identite">
              <Champ k="nom" label="Nom" {...p} readOnly />
              <Champ k="date_naissance" label="Date de naissance" type="date" {...p} />
              <Champ k="telephone" label="Téléphone" type="tel" {...p} required />
              <Champ k="email" label="E-mail" type="email" {...p} />
              <Champ k="adresse" label="Adresse" {...p} className="sm:col-span-2" />
            </Section>
            <Section n={2} title="Informations médicales" testid="fiche-enc-section-medical">
              {QUESTIONS.map(([k, d, label, ph]) => (
                <div key={k} className="rounded-xl border border-line p-3 sm:col-span-2">
                  <OuiNon label={label} value={f[k]} onChange={set(k)} testid={`fiche-${k.replace(/_/g, "-")}`} />
                  {f[k] === "oui" && <Champ k={d} label={ph} {...p} required className="mt-3" />}
                </div>
              ))}
              <label className="block sm:col-span-2"><span className={lbl}>Autres informations</span>
                <textarea data-testid="fiche-medical" rows={3} maxLength={1000} value={f.medical} onChange={(e) => set("medical")(e.target.value)} className={input} />
              </label>
            </Section>
            <Section n={3} title="Médecin traitant" testid="fiche-enc-section-medecin">
              <Champ k="medecin_nom" label="Nom et prénom" {...p} />
              <Champ k="medecin_tel" label="Téléphone" type="tel" {...p} />
            </Section>
            <Section n={4} title="Contact en cas d'urgence" tone="signal" testid="fiche-enc-section-urgence">
              <Champ k="urgence_nom" label="Nom et prénom" {...p} required />
              <Champ k="urgence_lien" label="Lien avec vous" placeholder="Ex. : Conjoint, Frère" {...p} required />
              <Champ k="urgence_tel" label="Téléphone" type="tel" {...p} required />
            </Section>
            <Section n={5} title="Équipement" testid="fiche-enc-section-equipement">
              <Champ k="taille_maillot" label="Taille de maillot" placeholder="Ex. : M, L, XL" {...p} />
            </Section>
            <Section n={6} title="Validation" testid="fiche-enc-section-validation">
              <label className="flex cursor-pointer items-start gap-3 sm:col-span-2" data-error={!!err.confirme}>
                <input type="checkbox" data-testid="fiche-confirme" checked={f.confirme} onChange={(e) => set("confirme")(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#D4AF37]" />
                <span className="text-chalk">Je confirme l'exactitude des informations fournies.<span className="text-signal"> *</span>
                  {err.confirme && <span data-testid="fiche-confirme-error" className="mt-1 block text-xs text-signal">{err.confirme}</span>}</span>
              </label>
              <Champ k="date_fiche" label="Date du jour" type="date" {...p} required />
            </Section>
            {serverErr && <p data-testid="fiche-enc-server-error" role="alert" className="rounded-xl border border-signal bg-signal/10 px-4 py-3 text-sm text-chalk">{serverErr}</p>}
            <button data-testid="fiche-enc-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-lime px-6 py-4 font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />}Envoyer la fiche
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
